import React from 'react';
import PropTypes from 'prop-types';

const DASH = '—';

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : DASH;

const yesNo = (value) => (value ? 'Yes' : 'No');

// Order.type is a JSON list, but legacy rows can hold a string or a stringified list.
const orderTypeList = (type) => {
  if (Array.isArray(type)) return type;
  if (typeof type !== 'string' || !type) return [];
  try {
    const parsed = JSON.parse(type);
    return Array.isArray(parsed) ? parsed : [type];
  } catch {
    return [type];
  }
};

const DetailRow = ({ label, value, mono = false }) => (
  <div className="detail-row">
    <span className="detail-label">{label}</span>
    <span className={mono ? 'detail-value mono' : 'detail-value'}>{value === null || value === undefined || value === '' ? DASH : value}</span>
  </div>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  mono: PropTypes.bool,
};

// Kit fulfillment IDs / tracking numbers, grouped per kit. Empty values render as
// a dash rather than being hidden — for support, "Tasso order not created yet" is
// itself the answer they came for.
const KitDetails = ({ dbOrder, tassoLabel }) => {
  if (!dbOrder || !Object.keys(dbOrder).length) return null;

  const types = orderTypeList(dbOrder.type);
  const hasMailman =
    types.includes('mailman') ||
    !!(dbOrder.mailman_status_detail || dbOrder.mailman_order_id || dbOrder.mailman_tracking_outbound_id || dbOrder.mailman_tracking_inbound_id);
  const hasTasso =
    types.includes('tasso') ||
    !!(
      dbOrder.tasso_status_detail ||
      dbOrder.tasso_order_id ||
      dbOrder.tasso_patient_id ||
      dbOrder.tasso_to_patient_tracking ||
      dbOrder.tasso_to_lab_tracking
    );
  const admin = dbOrder.admin_details;

  const statusValue = (detail, label) => {
    if (!detail) return DASH;
    const text = label || detail.label;
    return detail.code ? `${text} (${detail.code})` : text;
  };

  return (
    <div className="kit-details">
      <h3>Kit Order Details</h3>
      <div className="kit-detail-groups">
        <div className="kit-detail-group">
          <h4>Order</h4>
          <DetailRow label="Order ID" value={dbOrder.order_id} mono />
          <DetailRow label="Kit Type" value={types.length ? types.join(', ') : DASH} />
          <DetailRow label="Voucher ID" value={dbOrder.voucher_id} mono />
          <DetailRow label="Voucher Used" value={yesNo(dbOrder.is_voucher_used)} />
          <DetailRow label="MDI Case ID" value={dbOrder.case_id} mono />
          <DetailRow label="MDI Patient ID" value={dbOrder.mdi_patient_id} mono />
          <DetailRow label="DNA Fragmentation" value={yesNo(dbOrder.has_DNA_product)} />
        </div>

        {hasMailman && (
          <div className="kit-detail-group">
            <h4>Semen Analysis Kit (Mailman / MES)</h4>
            <DetailRow label="Status" value={statusValue(dbOrder.mailman_status_detail)} />
            <DetailRow label="Mailman Order ID" value={dbOrder.mailman_order_id} mono />
            <DetailRow label="Tracking — Kit to Patient" value={dbOrder.mailman_tracking_outbound_id} mono />
            <DetailRow label="Tracking — Kit to MES" value={dbOrder.mailman_tracking_inbound_id} mono />
            <DetailRow label="Shipped to Patient" value={formatDateTime(dbOrder.MES_outbound_shipment_date)} />
          </div>
        )}

        {hasTasso && (
          <div className="kit-detail-group">
            <h4>Hormone Panel Kit (Tasso)</h4>
            <DetailRow label="Status" value={statusValue(dbOrder.tasso_status_detail, tassoLabel)} />
            <DetailRow label="Tasso Order ID" value={dbOrder.tasso_order_id} mono />
            <DetailRow label="Tasso Patient ID" value={dbOrder.tasso_patient_id} mono />
            <DetailRow label="Tracking — To Patient" value={dbOrder.tasso_to_patient_tracking} mono />
            <DetailRow label="Tracking — To Lab" value={dbOrder.tasso_to_lab_tracking} mono />
          </div>
        )}

        {/* Staff-only block: the API omits admin_details entirely for patients. */}
        {admin && (
          <div className="kit-detail-group">
            <h4>Internal</h4>
            <DetailRow label="Order Source" value={admin.order_source} />
            <DetailRow label="Billing Type" value={admin.billing_type} />
            <DetailRow label="Clinic" value={admin.clinic_name} />
            <DetailRow label="Clinic Invoice" value={admin.clinic_invoice_status} />
            <DetailRow label="On Hold" value={yesNo(admin.is_hold)} />
            <DetailRow label="Review Status" value={admin.review_status} />
            <DetailRow label="Uses MDI" value={yesNo(admin.use_mdi)} />
            <DetailRow label="MDI Case Pending" value={yesNo(admin.is_mdi_case_creation_pending)} />
            <DetailRow
              label="Kit Shipped Email"
              value={admin.kit_shipped_email_sent ? formatDateTime(admin.kit_shipped_email_sent_at) : 'Not sent'}
            />
            <DetailRow label="Collection Guide Email" value={admin.delayed_1005_email_sent ? 'Sent' : 'Not sent'} />
          </div>
        )}
      </div>

      {/* Surfaced separately — it's a multi-line API error, not a one-line value. */}
      {admin?.mdi_case_creation_error && (
        <div className="kit-detail-error">
          <strong>MDI case creation error:</strong> {admin.mdi_case_creation_error}
        </div>
      )}
    </div>
  );
};

KitDetails.propTypes = {
  dbOrder: PropTypes.object,
  tassoLabel: PropTypes.string,
};

export default KitDetails;
